import { AxiosError } from 'axios';
import { apiService } from '@/shared/services/apiServices';
import { CANCEL_SERVICE_REQUEST_ERROR_MESSAGES } from '../errors/errorMessages';
import serviceRequestEndpoints from './serviceRequestEndpoints';

/** Input parameters for the cancel service request API call. */
export interface CancelServiceRequestParams {
  /** Internal UUID of the service request (`ViewServiceRequestDetail.id`). */
  serviceRequestId: string;
  /** Mandatory cancellation reason appended to `admin_notes` on the backend. */
  reason: string;
}

/** Result returned by {@link cancelServiceRequest}. */
export interface CancelServiceRequestResult {
  /** `true` when the backend accepted the cancellation (HTTP 2xx). */
  success: boolean;
  /** Localised Polish error message; present only when `success` is `false`. */
  error?: string;
}

/**
 * Maps an HTTP error response to a localised Polish error message.
 *
 * @param errorData - Raw response body from the API.
 * @param status - HTTP status code.
 * @returns A user-facing Polish error string.
 */
const extractErrorMessage = (errorData: unknown, status?: number): string => {
  if (status === 403) {
    return CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.UNAUTHORIZED;
  }
  if (status === 404) {
    return CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.NOT_FOUND;
  }
  if (status === 409) {
    const msg =
      typeof errorData === 'object' &&
      errorData !== null &&
      'message' in errorData
        ? String((errorData as { message: unknown }).message)
        : typeof errorData === 'string'
          ? errorData
          : '';
    if (msg.toLowerCase().includes('24 hour')) {
      return CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.CANCELLATION_WINDOW;
    }
    return CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.TRANSITION_NOT_ALLOWED;
  }
  if (status === 500) {
    return CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.INTERNAL_SERVER_ERROR;
  }
  return CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.DEFAULT_ERROR;
};

/**
 * Cancels a service request by PATCHing its status to `cancelled`.
 *
 * @remarks
 * Reuses the `UPDATE_SERVICE_REQUEST_STATUS` endpoint — the only difference from
 * a regular status update is that `new_status` is fixed to `'cancelled'` and
 * `admin_notes` (the cancellation reason) is mandatory.
 *
 * Error mapping:
 * - `403` → client does not own this request or lacks the `service_request_cancel` AVP permission
 * - `404` → service request not found
 * - `409` containing `"24 hour"` → {@link CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.CANCELLATION_WINDOW}
 * - `409` other → {@link CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.TRANSITION_NOT_ALLOWED}
 * - `500` → {@link CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.INTERNAL_SERVER_ERROR}
 *
 * @param params - See {@link CancelServiceRequestParams}.
 * @returns `{ success: true }` on success, or `{ success: false, error }` with a localised message.
 */
export const cancelServiceRequest = async ({
  serviceRequestId,
  reason,
}: CancelServiceRequestParams): Promise<CancelServiceRequestResult> => {
  const trimmedId = serviceRequestId?.trim();
  const trimmedReason = reason.trim();

  if (!trimmedId) {
    return {
      success: false,
      error: CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.DEFAULT_ERROR,
    };
  }

  try {
    const result = await apiService.patchAPI(
      serviceRequestEndpoints.UPDATE_SERVICE_REQUEST_STATUS(trimmedId),
      { new_status: 'cancelled', admin_notes: trimmedReason }
    );

    if (!result.error) {
      return { success: true };
    }

    if (result.isNetworkError) {
      return {
        success: false,
        error: CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.NETWORK_ERROR,
      };
    }

    return {
      success: false,
      error: extractErrorMessage(result.data, result.status),
    };
  } catch (error) {
    if (error instanceof AxiosError) {
      if (!error.response && error.request) {
        return {
          success: false,
          error: CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.NETWORK_ERROR,
        };
      }
      return {
        success: false,
        error: extractErrorMessage(
          error.response?.data,
          error.response?.status
        ),
      };
    }
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : CANCEL_SERVICE_REQUEST_ERROR_MESSAGES.UNEXPECTED_ERROR,
    };
  }
};
